import { Box, styled } from "@mui/material";

import { useAppSelector } from "app/redux-store-interactions";
import { useHasOpenDrawer } from "contexts/drawers";
import { useHasOpenPanels } from "contexts/sidePanels";
import { selectPicker } from "features/render";
import { Picker } from "features/render/types";
import DpadIcon from "media/icons/DPAD.svg?react";
import DpadTIcon from "media/icons/DPAD_T.svg?react";

import {
    CursorVisual,
    DpadStyle,
    PICK_TARGET_OFFSET,
    selectCursorActive,
    selectCursorPosition,
    selectCursorVisual,
    selectDpadStyle,
    selectIndicatorPosition,
    selectIndicatorTransform,
} from "./mobileMeasureSlice";

const INDICATOR_SIZE = 28;

const CursorContainer = styled(Box)({
    position: "absolute",
    pointerEvents: "none",
    zIndex: 1100,
    willChange: "transform",
});

const dpadComponents: Record<DpadStyle, React.FC> = {
    normal: () => <DpadIcon width={51} height={51} />,
    transparent: () => <DpadTIcon width={51} height={51} />,
};

// ── Indicator SVGs matching desktop cursors ──

/** Universal no-hit / not-measurable indicator: red X (matches desktop "rotatedCross") */
function NoHitCrossIndicator() {
    const s = INDICATOR_SIZE;
    return (
        <svg width={s} height={s} viewBox="0 0 24 24">
            <path d="M6 6L18 18" stroke="#E05252" strokeWidth={5} strokeLinecap="round" />
            <path d="M18 6L6 18" stroke="#E05252" strokeWidth={5} strokeLinecap="round" />
            <path d="M6 6L18 18" stroke="#570F0F" strokeWidth={2} strokeLinecap="round" />
            <path d="M18 6L6 18" stroke="#570F0F" strokeWidth={2} strokeLinecap="round" />
        </svg>
    );
}

/** Coordinate on-surface: bullseye (matches desktop "coordinatesSurface") */
function CoordinateSurfaceIndicator() {
    const s = 32;
    return (
        <svg width={s} height={s} viewBox={`${-s / 2} ${-s / 2} ${s} ${s}`}>
            <circle cx={0} cy={0} r={15} stroke="#0F3" strokeWidth={2} fill="none" />
            <circle cx={0} cy={0} r={10} fill="#000" />
            <circle cx={0} cy={0} r={2} fill="#0F3" />
        </svg>
    );
}

/** PointLine/Dimension: green crosshair + center dot (matches desktop "pointSnap") */
function PointSnapCrosshairIndicator({ snap = false }: { snap?: boolean }) {
    const s = INDICATOR_SIZE;
    const r = snap ? 6 : 3;
    return (
        <svg width={s} height={s} viewBox="0 0 24 24">
            <path d="M12 3V21" stroke="black" strokeOpacity={0.38} strokeWidth={5} strokeLinecap="round" />
            <path d="M12 3V21" stroke="#0F3" strokeWidth={2} strokeLinecap="round" />
            <path d="M21 12L3 12" stroke="black" strokeOpacity={0.38} strokeWidth={5} strokeLinecap="round" />
            <path d="M21 12L3 12" stroke="#0F3" strokeWidth={2} strokeLinecap="round" />
            <circle cx={12} cy={12} r={r} fill="#092A25" stroke="#0F3" strokeWidth={2} />
        </svg>
    );
}

/** Edge hover: 2-sided crosshair (matches desktop "edge" cursor). */
function EdgeCrosshairIndicator() {
    const s = INDICATOR_SIZE;
    return (
        <svg width={s} height={s} viewBox="0 0 24 24">
            <path d="M12 5V19" stroke="black" strokeOpacity={0.38} strokeWidth={5} strokeLinecap="round" />
            <path d="M12 5V19" stroke="#0F3" strokeWidth={2} strokeLinecap="round" />
            <path d="M22 12L2 12" stroke="black" strokeOpacity={0.38} strokeWidth={5} strokeLinecap="round" />
            <path d="M22 12L2 12" stroke="#0F3" strokeWidth={2} strokeLinecap="round" />
        </svg>
    );
}

function TransparentCircleIndicator() {
    const s = INDICATOR_SIZE;
    return (
        <svg width={s} height={s} viewBox={`${-s / 2} ${-s / 2} ${s} ${s}`}>
            <circle cx={0} cy={0} r={12} fill="rgba(0, 255, 51, 0.1)" stroke="#0F3" strokeWidth={1.5} />
        </svg>
    );
}

/** Placed/selected indicator (matches desktop dimension snap+selected state) */
function PlacedIndicator() {
    const s = INDICATOR_SIZE;
    return (
        <svg width={s} height={s} viewBox="0 0 24 24">
            <rect width={20} height={20} x={2} y={2} fill="#232729" opacity={0.5} rx={4} />
            <rect width={6} height={6} x={9} y={9} fill="#0F3" rx={2} />
        </svg>
    );
}

type PickerIndicatorMap = Record<CursorVisual, React.FC>;

const laserIndicators: PickerIndicatorMap = {
    crosshair: () => <PointSnapCrosshairIndicator />,
    pointSnap: () => <PointSnapCrosshairIndicator snap />,
    surfaceSnap: () => <PointSnapCrosshairIndicator />,
    noHit: NoHitCrossIndicator,
    placed: PlacedIndicator,
    transparentCircle: TransparentCircleIndicator,
};

const coordinateIndicators: PickerIndicatorMap = {
    crosshair: CoordinateSurfaceIndicator,
    pointSnap: CoordinateSurfaceIndicator,
    surfaceSnap: CoordinateSurfaceIndicator,
    noHit: NoHitCrossIndicator,
    placed: PlacedIndicator,
    transparentCircle: TransparentCircleIndicator,
};

const pointLineIndicators: PickerIndicatorMap = {
    crosshair: () => <PointSnapCrosshairIndicator />,
    pointSnap: () => <PointSnapCrosshairIndicator snap />,
    surfaceSnap: () => <PointSnapCrosshairIndicator />,
    noHit: NoHitCrossIndicator,
    placed: PlacedIndicator,
    transparentCircle: TransparentCircleIndicator,
};

const dimensionIndicators: PickerIndicatorMap = {
    crosshair: () => <PointSnapCrosshairIndicator />,
    pointSnap: EdgeCrosshairIndicator,
    surfaceSnap: () => <PointSnapCrosshairIndicator />,
    noHit: NoHitCrossIndicator,
    placed: PlacedIndicator,
    transparentCircle: TransparentCircleIndicator,
};

const defaultIndicators: PickerIndicatorMap = {
    crosshair: () => <PointSnapCrosshairIndicator />,
    pointSnap: () => <PointSnapCrosshairIndicator snap />,
    surfaceSnap: () => <PointSnapCrosshairIndicator />,
    noHit: NoHitCrossIndicator,
    placed: PlacedIndicator,
    transparentCircle: TransparentCircleIndicator,
};

function getIndicatorMap(picker: Picker): PickerIndicatorMap {
    switch (picker) {
        case Picker.OutlineLaser:
            return laserIndicators;
        case Picker.Coordinates:
            return coordinateIndicators;
        case Picker.PointLine:
            return pointLineIndicators;
        case Picker.Dimension:
            return dimensionIndicators;
        default:
            return defaultIndicators;
    }
}

export function MobileMeasureCursor() {
    const active = useAppSelector(selectCursorActive);
    const position = useAppSelector(selectCursorPosition);
    const indicatorPos = useAppSelector(selectIndicatorPosition);
    const indicatorTransform = useAppSelector(selectIndicatorTransform);
    const visual = useAppSelector(selectCursorVisual);
    const dpadStyle = useAppSelector(selectDpadStyle);
    const picker = useAppSelector(selectPicker);
    const hasOpenDrawer = useHasOpenDrawer();
    const hasOpenPanels = useHasOpenPanels();

    if (!active || !position || hasOpenDrawer || hasOpenPanels) {
        return null;
    }

    const indicatorMap = getIndicatorMap(picker);
    const Indicator = indicatorMap[visual];
    const Dpad = dpadComponents[dpadStyle];

    const isNoHit = visual === "noHit";
    const indX = isNoHit ? position.x + PICK_TARGET_OFFSET.x : (indicatorPos?.x ?? position.x + PICK_TARGET_OFFSET.x);
    const indY = isNoHit ? position.y + PICK_TARGET_OFFSET.y : (indicatorPos?.y ?? position.y + PICK_TARGET_OFFSET.y);

    const surfaceAlign = !isNoHit && !!indicatorTransform;

    return (
        <>
            <CursorContainer
                sx={{
                    filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.20))",
                    left: position.x,
                    top: position.y,
                    transform: "translate(-50%, -50%)",
                }}
            >
                <Dpad />
            </CursorContainer>

            <div
                style={{
                    position: "absolute",
                    left: indX,
                    top: indY,
                    pointerEvents: "none",
                    zIndex: 1100,
                    transform: surfaceAlign ? indicatorTransform! : undefined,
                    transformOrigin: "0 0",
                }}
            >
                <div style={{ transform: "translate(-50%, -50%)" }}>
                    <Indicator />
                </div>
            </div>
        </>
    );
}
